import React from 'react';

import { setHours, setMinutes } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';

import { ia } from 'lib/helpers/utility';
import useTimezone from 'lib/hooks/useTimezone';
import { formatDate } from 'lib/helpers/dateUtils';

import Icon from 'components/shared/Icon/Icon';
import Header from 'components/shared/shared/Header';
import TimePicker from 'components/shared/TimePicker/TimePicker';

// Whole-day placeholders shown until a time is actually chosen. Deliberately plain wall-clock strings
// rather than Dates: the previous version built them from `new Date()` and then rendered the resulting
// *instant* in `timezone`, so browser-local midnight displayed as 9:00 PM the previous day for anyone
// whose selected zone differs from their machine's. A placeholder has no instant to convert.
const INITIAL_START_TIME = '00:00';
const INITIAL_END_TIME = '23:59';

const TimeSelector = ({
  isTime = false,
  isTimeRange = false,
  isDateRange = false,
  value,
  onChange,
  baseDate = null, // The date from DateSelector to combine with time
  showTimezone = true,
  timezone: timezoneProp = null
}) => {
  const userTimezone = useTimezone({ enabled: !timezoneProp });
  const timezone = timezoneProp || userTimezone;

  if (!isTime && !isTimeRange) return null;

  if (isTimeRange || isDateRange) {
    const startDate = new Date(value?.[0]);
    const endDate = new Date(value?.[1]);

    const startTimeValue = value?.[0]
      ? formatDate(startDate, timezone, 'HH:mm')
      : INITIAL_START_TIME;
    const endTimeValue = value?.[1] ? formatDate(endDate, timezone, 'HH:mm') : INITIAL_END_TIME;

    const handleStartTimeChange = (newTime) => {
      if (!newTime) {
        onChange([null, value?.[1] || null]);
        return;
      }

      const baseDateTime = ia(baseDate) ? baseDate?.[0] : baseDate || new Date();
      const [hours, minutes] = newTime.split(':').map(Number);
      const baseDateInTz = toZonedTime(new Date(baseDateTime), timezone);
      const newDateZoned = setMinutes(setHours(baseDateInTz, hours), minutes);

      onChange([fromZonedTime(newDateZoned, timezone), value?.[1] || null]);
    };

    const handleEndTimeChange = (newTime) => {
      if (!newTime) {
        onChange([value?.[0] || null, null]);
        return;
      }

      const baseDateTime = ia(baseDate) ? baseDate?.[1] : baseDate || new Date();
      const [hours, minutes] = newTime.split(':').map(Number);
      const baseDateInTz = toZonedTime(new Date(baseDateTime), timezone);
      const newDateZoned = setMinutes(setHours(baseDateInTz, hours), minutes);

      onChange([value?.[0] || null, fromZonedTime(newDateZoned, timezone)]);
    };

    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-end gap-1">
          <div className="flex w-full flex-col">
            <Header label="Start time" required />
            <TimePicker
              timezone={timezone}
              date={value?.[0] || (ia(baseDate) ? baseDate[0] : baseDate)}
              value={startTimeValue}
              onChange={handleStartTimeChange}
              hasOptions={false}
              options={null}
              showTimezone={showTimezone}
            />
          </div>
          <div className="flex min-w-[28px] items-center justify-center">
            <Icon
              icon="minus-linear"
              color="neutral"
              size={20}
              shade={700}
              className="!mb-[10px]"
            />
          </div>
          <div className="flex w-full flex-col">
            <Header label="End time" required />
            <TimePicker
              timezone={timezone}
              date={value?.[1] || (ia(baseDate) ? baseDate[1] : baseDate)}
              value={endTimeValue}
              onChange={handleEndTimeChange}
              hasOptions={false}
              showTimezone={showTimezone}
            />
          </div>
        </div>
      </div>
    );
  }

  if (isTime) {
    // Extract time from value or baseDate
    const date = new Date(value);
    const timeValue = value ? formatDate(date, timezone, 'HH:mm') : INITIAL_START_TIME;

    const handleTimeChange = (newTime) => {
      if (!newTime) {
        onChange(null);
        return;
      }

      const baseDateTime = baseDate || new Date();
      const [hours, minutes] = newTime.split(':').map(Number);
      const baseDateInTz = toZonedTime(new Date(baseDateTime), timezone);
      const newDateZoned = setMinutes(setHours(baseDateInTz, hours), minutes);

      onChange(fromZonedTime(newDateZoned, timezone));
    };

    return (
      <TimePicker
        timezone={timezone}
        date={value || baseDate}
        value={timeValue}
        required
        label="Time"
        hasOptions={false}
        options={null}
        onChange={handleTimeChange}
        showTimezone={showTimezone}
      />
    );
  }

  return null;
};

export default TimeSelector;
